import { memo, useCallback, useEffect, useMemo, useState } from "react";
import ResponsiveAppBar from "@publicPortal/components/responsiveAppBar/ResponsiveAppBar";
import Board from "@publicPortal/components/board/Board";
import PASummaryTabContent from "./tabContent/PASummaryTabContent";
import { useParams } from "react-router-dom";
import PACommentsTabContent from "./tabContent/PACommentsTabContent";
import DocumentsTable from "@mirage/components/documentsTable/DocumentsTable";
import { BackButton } from "@publicPortal/components/BackButton/BackButton";
import PlanningApplicationTabs, { PlanningTabs, PublicPlanningApplicationTabs } from "./PlanningApplicationTabs";
import RemoveSaveBookmark from "@publicPortal/components/customBookmark/RemoveSaveBookmark";
import SaveBookmark from "@publicPortal/components/customBookmark/SaveBookmark";
import { UserService } from "@publicPortal/services/UserService";
import { TrackedApplicationService } from "@publicPortal/services/TrackedApplicationService";
import { IPlanningApplicationDetails } from "@shared/publicportal/contracts/IPlanningApplicationDetails";
import PlanningApplicationsService from "@publicPortal/services/PlanningApplicationsService";
import { VerifyCommentsDisabled } from "@shared/publicportal/helpers/VerifyCommentsDisabled";
import PAConstraintsTabContent from "./tabContent/PAConstraintsTabContent";
import PAConsultationsTabContent from "./tabContent/PAConsultationsTabContent";
import { PAMapTab } from "./tabContent/PAMapTab";
import { usePlanningApplicationDetailsStore } from "@publicPortal/store/store";
import { RecordDetailsTabNames } from "@publicPortal/utils/enums";
import useAlertService from "@mirage/hooks/useAlertService";
import { IUser } from "@shared/mirage/core/contracts/IUser";
import { PublicConsulteeTokenDetails } from "@shared/mirage/core/contracts/User";
import UserCustomProperty from "@shared/mirage/utils/getUserCustomProperty";
import { useDeviceDetect } from "@mirage/hooks/useDeviceDetect";
import { MobileTabPicker } from "@publicPortal/components/mobileTabPicker/MobileTabPicker";
import useSessionStorage from "@mirage/hooks/useSessionStorage";
import { PPConstants } from "@shared/publicportal/constants";
import { GridColDef } from "@mui/x-data-grid-premium";

const trackedApplicationService = new TrackedApplicationService();

const PlanningApplication = memo(() => {
    const planningAppService = new PlanningApplicationsService();
    const user: Partial<IUser> = UserService.getLoggedInUser();
    const { applicationId } = useParams();
    const [planningApp, setPlanningApp] = useState<IPlanningApplicationDetails | null>(null);
    const [loading, setLoading] = useState(true);
    const [isTracked, setIsTracked] = useState({ tracked: false, id: 0 });
    const [trackChanged, setTrackChanged] = useState(false);
    const currentTab = usePlanningApplicationDetailsStore((s) => s.planningApplicationDetails);
    const updatePlanningApplicationTab = usePlanningApplicationDetailsStore((s) => s.updatePlanningApplicationTab);
    const showAlert = useAlertService();
    const consulteeTokenString = localStorage.getItem("verifyConsultee.token");
    const consulteeToken: PublicConsulteeTokenDetails = consulteeTokenString && JSON.parse(consulteeTokenString);
    const consulteeId = UserCustomProperty.getUserCustomPropertyValue(user, "consulteeId");
    const { isMobile } = useDeviceDetect();
    const currentApplicationTab: PlanningTabs = planningApp?.id ? currentTab[planningApp?.id] || RecordDetailsTabNames.Summary : RecordDetailsTabNames.Summary;
    const [sessionValue] = useSessionStorage(PPConstants.AppName);
    const hideConstraintsTab = !!sessionValue.clientSettings?.HIDE_CONSTRAINTS_TAB;
    const visibleDocumentsTableColumns = sessionValue.clientSettings.DOCUMENTS_TABLE_COLUMNS;

    useEffect(() => {
        const getPlanningApp = async () => {
            setLoading(true);
            const response = await planningAppService.getPlanningApplicationById(Number(applicationId));
            if (!response) {
                setLoading(false);
                return;
            }
            setPlanningApp(response);
            setLoading(false);
        };

        getPlanningApp();

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [applicationId]);

    useEffect(() => {
        const getTracked = async () => {
            if (!user || !user.id) {
                return;
            }
            const result = await trackedApplicationService.getTracked();
            if (Array.isArray(result)) {
                const filteredApplication = result?.filter((tracked) => Number(tracked.paId) === Number(applicationId));
                if (filteredApplication.length) {
                    setIsTracked({ tracked: true, id: filteredApplication[0].id });
                    return;
                }
            }
            setIsTracked({ tracked: false, id: 0 });
        };

        getTracked();

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [trackChanged]);

    const onTabSelect = useCallback(
        (newTab: string) => {
            if (!planningApp?.id) {
                return;
            }
            updatePlanningApplicationTab(planningApp.id, newTab as RecordDetailsTabNames);
        },
        [planningApp?.id, updatePlanningApplicationTab]
    );
    const saveTrackedApplication = useCallback(async () => {
        if (planningApp?.id) {
            const result = await trackedApplicationService.createTrackedApplication({ paId: planningApp.id });
            if (result?.errorMsg) {
                showAlert(result?.errorMsg, "error");
            } else {
                showAlert("You successfully tracked application", "success");
            }
        }
    }, [planningApp?.id, showAlert]);
    const handleSaveTracked = useCallback(async () => {
        await saveTrackedApplication();
        setTrackChanged((prev) => !prev);
    }, [saveTrackedApplication]);

    const handleDeleteTracked = useCallback(async () => {
        if (isTracked.id) {
            const result = await trackedApplicationService.deleteTrackedApplication(isTracked.id);
            if (result.errorMsg) {
                showAlert(result.errorMsg, "error");
            } else {
                showAlert("You successfully untracked application", "success");
            }
        }
        setTrackChanged((prev) => !prev);
    }, [isTracked.id, showAlert]);

    const displayFor = useMemo(() => ({ entityRecord: { entityName: "P_APPLICATION", recordId: Number(applicationId) } }), [applicationId]);
    const overrideColumns = useCallback(
        (existingColumns: GridColDef[]) => {
            if (!Array.isArray(visibleDocumentsTableColumns) || visibleDocumentsTableColumns.length == 0) {
                return existingColumns;
            }
            const alwaysVisibleColumns = ["action", "folder"];
            return existingColumns.filter((col) => visibleDocumentsTableColumns.find((visibleCol) => visibleCol == col.field || alwaysVisibleColumns.includes(col.field)));
        },
        [visibleDocumentsTableColumns]
    );

    if (!planningApp && !loading) {
        return (
            <>
                <ResponsiveAppBar />
                <Board headerText="Planning Application not found" loadingHeaderText={loading} size="big">
                    <BackButton />
                </Board>
            </>
        );
    }

    return (
        <>
            <ResponsiveAppBar />
            <Board size="big" headerText={"Planning Application " + (planningApp?.name || "(none)")} loadingHeaderText={loading}>
                {!consulteeId ? <BackButton /> : null}
                {getBoardContent()}
            </Board>
        </>
    );

    function getBoardContent() {
        return (
            <>
                {getBookmark()}
                {isMobile ? (
                    <MobileTabPicker label="Current Tab" currentTab={currentApplicationTab} tabs={PublicPlanningApplicationTabs} onTabSelect={onTabSelect} />
                ) : (
                    <PlanningApplicationTabs currentTab={currentApplicationTab} handleChangeTab={onTabSelect} hideConstraintsTab={hideConstraintsTab} />
                )}
                {getTabContent()}
            </>
        );
    }

    function getTabContent() {
        switch (currentApplicationTab) {
            case "Summary":
                return <PASummaryTabContent loading={loading} planningApp={planningApp} />;
            case "Constraints":
                return !hideConstraintsTab ? <PAConstraintsTabContent paId={planningApp?.id} service={planningAppService} /> : null;
            case "Documents":
                return <DocumentsTable publicOnly={true} readOnly stateKey="planning-application-documents" displayFor={displayFor} overrideColumns={overrideColumns} />;
            case "Map":
                return <PAMapTab planningApp={planningApp} />;
            case "Comments":
                return <PACommentsTabContent paId={planningApp?.id} newCommentsDisabledReason={checkCommentsAreAllowed()} user={user} />;
            case "Consultations":
                return <PAConsultationsTabContent paId={planningApp?.id} newCommentsDisabledReason={checkCommentsAreAllowed()} user={user} />;
            default:
                return <></>;
        }
    }

    function getBookmark() {
        if (isTracked.tracked) {
            return <RemoveSaveBookmark action={handleDeleteTracked} tooltipMessage="Remove Track" userId={user?.id} />;
        }
        return <SaveBookmark action={handleSaveTracked} tooltipMessage="Add Track" userId={user?.id} />;
    }

    function checkCommentsAreAllowed() {
        const commentsByStatusDisabled = VerifyCommentsDisabled(planningApp?.applicationStatus);
        if (commentsByStatusDisabled) {
            return "This application is closed for further comments.";
        }
        if (consulteeId && consulteeToken?.paId && consulteeToken.paId !== planningApp?.id) {
            return "You are not allowed to consult on this application.";
        }
        return "";
    }
});

export default PlanningApplication;
